import { useEffect, useRef } from "react";
import { currencyMeta, currencyName } from "../currencies/meta";
import { formatDate, type AppLocale } from "../i18n/format";
import { CalculatorIcon, ChevronDownIcon, DotsVerticalIcon } from "./Icons";

/**
 * One currency row.
 *
 * ARIA ROLES ARE LOAD-BEARING — DO NOT REMOVE THEM.
 * The layout layer changes `display` on <tr>, <th> and <td> to lay rows out as
 * grids. In most browsers that STRIPS the implicit table roles, silently
 * turning a data table into a pile of generic boxes for assistive tech. The
 * explicit role="row"/"rowheader"/"cell" attributes below survive any `display`
 * value, which is what lets layouts stay pure CSS without costing semantics.
 * ConvertScreen.test.tsx asserts they are present in every layout.
 *
 * The markup itself never varies by layout: same elements, same order, same
 * class names. Only base-vs-quote — which is data, not appearance — changes
 * what is rendered.
 */

/**
 * Steps the amount down in size as it gets longer.
 *
 * A fifteen-digit amount is 19 characters once grouped, which at 320px is wider
 * than the whole row. Truncating a number is not an option — a clipped figure is
 * a wrong figure — so the type shrinks instead. The thresholds are character
 * counts of the already-formatted string, and the sizes themselves stay tokens.
 */
function lengthTier(text: string): "md" | "lg" | "xl" | "xxl" | undefined {
  const n = text.length;
  if (n >= 20) return "xxl";
  if (n >= 16) return "xl";
  if (n >= 12) return "lg";
  if (n >= 9) return "md";
  return undefined;
}

interface Props {
  readonly code: string;
  readonly locale: AppLocale;
  readonly isBase: boolean;
  /** Formatted value for quote rows. */
  readonly amountDisplay: string;
  /** Raw editable string for the base row. */
  readonly amountInput: string;
  /** Terse visual form, e.g. "1 SAR = 0.2667 USD". */
  readonly unitRateText: string;
  /** Spelled-out form for screen readers. */
  readonly unitRateAccessible: string;
  /** Set when this row's quote date is older than the freshest row's. */
  readonly staleDate: string | null;
  readonly coverageState: "ok" | "caveated" | "withheld";
  /** The one sentence, shown on tap. Null when there is nothing to say. */
  readonly coverageNote: string | null;
  readonly coverageLabel: string;
  readonly noteOpen: boolean;
  readonly onToggleNote: () => void;
  /** Caret/selection to project into the DOM, owned by the field state machine. */
  readonly selection: { readonly start: number; readonly end: number } | null;
  /** Briefly marked after the picker was asked where this currency is. */
  readonly revealed: boolean;
  readonly dragging: boolean;
  readonly dragOffset: number;
  /** How far this row moves aside to open the gap. Zero for the dragged row. */
  readonly neighbourShift: number;
  /** Distance travelled toward the inline-start edge; always non-negative. */
  readonly swipeProgress: number;
  readonly willRemove: boolean;
  readonly gestureProps: React.ComponentProps<"tr">;
  readonly onRowKeyDown: (event: React.KeyboardEvent) => void;
  readonly onFieldTap: () => void;
  readonly onMakeBase: () => void;
  readonly onAction: () => void;
  readonly onPickCurrency: () => void;
}

export function CurrencyRow({
  code,
  locale,
  isBase,
  amountDisplay,
  amountInput,
  unitRateText,
  unitRateAccessible,
  staleDate,
  coverageState,
  coverageNote,
  coverageLabel,
  noteOpen,
  onToggleNote,
  selection,
  revealed,
  dragging,
  dragOffset,
  neighbourShift,
  swipeProgress,
  willRemove,
  gestureProps,
  onRowKeyDown,
  onFieldTap,
  onMakeBase,
  onAction,
  onPickCurrency,
}: Props) {
  const meta = currencyMeta(code);
  const name = currencyName(code, locale);
  const shownAmount = isBase ? amountInput : amountDisplay;
  const inputRef = useRef<HTMLInputElement>(null);

  /*
   * Projects the field state machine's caret onto the DOM.
   *
   * Runs after the value changes so the range is applied to the new text, not
   * the old. This is the only place selectionStart/End is touched — the machine
   * owns where the caret is, and the input merely displays it.
   */
  useEffect(() => {
    if (!isBase || !selection) return;
    const el = inputRef.current;
    if (!el) return;
    if (el.selectionStart !== selection.start || el.selectionEnd !== selection.end) {
      el.setSelectionRange(selection.start, selection.end);
    }
  }, [isBase, selection, amountInput]);

  const amountLabel =
    locale === "ar" ? `المبلغ بـ${name}` : `Amount in ${name}`;

  const swapLabel =
    locale === "ar"
      ? `اجعل ${name} العملة الأساسية`
      : `Make ${name} the base currency`;

  const actionLabel = isBase
    ? locale === "ar"
      ? "الآلة الحاسبة"
      : "Calculator"
    : locale === "ar"
      ? `خيارات ${name}`
      : `Options for ${name}`;

  return (
    <tr
      className="row"
      role="row"
      data-code={code}
      data-revealed={revealed ? "true" : undefined}
      data-base={isBase ? "true" : undefined}
      data-dragging={dragging ? "true" : undefined}
      data-shifted={neighbourShift !== 0 ? "true" : undefined}
      data-swiping={swipeProgress > 0 ? "true" : undefined}
      data-will-remove={willRemove ? "true" : undefined}
      /*
       * The lift carries NO horizontal component. Any would force an RTL
       * direction choice with no good answer; scale, shadow and elevation say
       * "lifted" on their own. The swipe travels toward the inline-start edge,
       * so its sign is flipped under RTL and the action is revealed at
       * inline-end — mirroring for free.
       */
      style={
        {
          "--drag-y": `${dragOffset}px`,
          "--shift-y": `${neighbourShift}px`,
          "--swipe-x": `${swipeProgress}px`,
        } as React.CSSProperties
      }
      onKeyDown={onRowKeyDown}
      {...gestureProps}
    >
      <th className="row__id" role="rowheader" scope="row">
        {meta.flag ? (
          <img
            className="row__flag"
            src={`/flags/${meta.flag}.svg`}
            alt=""
            width={32}
            height={24}
            /* Eager, not lazy: these are a handful of ~2 kB icons above the
               fold. Deferring them buys nothing and risks an empty box on
               first paint, which on this screen reads as a broken row. */
            decoding="async"
          />
        ) : (
          <span className="row__flag row__flag--none" aria-hidden="true" />
        )}

        <span className="row__identity">
          <span className="row__code-line">
            <span className="row__code" dir="ltr">
              {code}
            </span>
            {isBase ? (
              <button
                type="button"
                className="row__chevron-btn"
                data-gesture="ignore"
                onClick={onPickCurrency}
                aria-label={locale === "ar" ? "تغيير العملة الأساسية" : "Change base currency"}
              >
                <ChevronDownIcon className="row__chevron" />
              </button>
            ) : null}
          </span>
          <span className="row__name">{name}</span>
        </span>

        {/* Tap anywhere on a quote row to make it the base. An overlay button
            rather than a click handler on <tr>, so it is reachable by keyboard
            and announced as the control it is. */}
        {isBase ? null : (
          <button type="button" className="row__swap" onClick={onMakeBase}>
            <span className="visually-hidden">{swapLabel}</span>
          </button>
        )}
      </th>

      <td
        className="row__value"
        role="cell"
        data-len={lengthTier(shownAmount)}
        data-coverage={coverageState === "ok" ? undefined : coverageState}
      >
        {coverageState === "withheld" ? (
          /*
           * No figure at all — not a zero, not a dash pretending to be one.
           * The sentence takes the number's place so the row explains itself
           * rather than looking broken.
           */
          <span className="row__withheld">{coverageNote}</span>
        ) : isBase ? (
          <input
            ref={inputRef}
            className="row__amount row__amount--input"
            /* The field owns its own taps — see useRowGestures. */
            data-gesture="ignore"
            /* Deliberately text, not number: type="number" adds a spinner,
               mishandles grouping separators, and behaves badly in RTL. */
            type="text"
            /*
             * readOnly + inputMode="none" suppress the OS keyboard entirely.
             * Every keystroke arrives from our own pad, which is what makes
             * "one press clears everything" possible and keeps the rows
             * visible while typing. The field stays focusable, selectable and
             * announced — it is a display surface, not a disabled control.
             */
            readOnly
            inputMode="none"
            autoComplete="off"
            spellCheck={false}
            dir="ltr"
            value={amountInput}
            onClick={onFieldTap}
            onFocus={onFieldTap}
            aria-label={amountLabel}
          />
        ) : (
          <span className="row__amount" dir="ltr">
            {amountDisplay}
          </span>
        )}

        {/*
         * No unit rate on the base row: "1 SAR = 1.0000 SAR" is a tautology.
         *
         * And none on a WITHHELD row either. The screenshot caught this: the
         * amount was correctly suppressed while "1 SAR = 32.5600 SYP" went on
         * rendering underneath — a figure derived from exactly the rate we had
         * just declined to stand behind. Withholding has to cover every number
         * on the row, not the largest one.
         */}
        {isBase || coverageState === "withheld" ? null : (
          <span className="row__unit" dir="ltr" aria-hidden="true">
            {unitRateText}
            {staleDate ? (
              <>
                {" · "}
                {/* Its own dir="auto" inside the LTR unit-rate run: an Arabic
                    month name in an LTR paragraph gets reordered — see the
                    note in RowSheet for the measurement that found it. */}
                <time className="row__stale" dir="auto" dateTime={staleDate}>
                  {formatDate(staleDate, locale, { day: "2-digit", month: "short" })}
                </time>
              </>
            ) : null}
          </span>
        )}

        {/* Spelled out, so the row reads as a sentence rather than as symbols. */}
        {isBase || coverageState === "withheld" ? null : (
          <span className="visually-hidden">{unitRateAccessible}</span>
        )}

        {/*
         * The caveat marker. A permanent line would be read for a week and
         * skipped forever after — and worse, it would train the eye to skip
         * notices in this app generally. The mark stays present without being
         * spent; the sentence appears on demand.
         *
         * NOT COLOUR ALONE: a glyph plus a hairline ring, legible in greyscale.
         */}
        {coverageState === "caveated" && coverageNote ? (
          <button
            type="button"
            className="row__note-btn"
            data-gesture="ignore"
            aria-label={coverageLabel}
            aria-expanded={noteOpen}
            onClick={(event) => {
              event.stopPropagation();
              onToggleNote();
            }}
          >
            <span className="row__note-mark" aria-hidden="true">
              i
            </span>
          </button>
        ) : null}

        {coverageState === "caveated" && noteOpen && coverageNote ? (
          <span className="row__note" role="note">
            {coverageNote}
          </span>
        ) : null}
      </td>

      <td className="row__action" role="cell">
        <button
          type="button"
          className="row__action-btn"
          data-gesture="ignore"
          onClick={onAction}
          aria-label={actionLabel}
        >
          {isBase ? <CalculatorIcon /> : <DotsVerticalIcon />}
        </button>
      </td>
    </tr>
  );
}
